// this exists to wrap the casl library and provide the functionality we need
// not sure if we'd have been better off just implementing our own
// given we only need a small subset of the libraries functionality
// but the usage is restricted to here if we decide to do so

/* eslint-disable no-underscore-dangle */
import {
  Ability,
  AbilityBuilder,
  ForbiddenError,
  subject
} from "@casl/ability";

type PermissionEnforceType = 
  { $in?: string[], $exists?: boolean }

interface PermissionEnforceDefinition { [fieldName:string]: PermissionEnforceType | undefined; }

interface PermissionDataFilterDefinition { [fieldName:string]: PermissionFieldDataFilter | undefined; }

export interface PermissionDataFilter { 
  getForField(fieldName: string) : PermissionFieldDataFilter
}
export type PermissionFieldDataFilter = 
  { $in: string[], $any?: false } | { $any: true } 

export const createCaslAdapter = <T>(
  permissionToSubjectAction: (permission: T) => ({ subject: string, action: string}),
  userContextMessage: string
  ) => {
  const dataFilters:{[permission:string]: PermissionDataFilterDefinition | undefined } = {};
  const abilityBuilder = new AbilityBuilder(Ability);

  const allow = (
    permission: T,
    enforce?: PermissionEnforceDefinition,
    filter?: PermissionDataFilterDefinition
  ) => {
    const { subject, action } = permissionToSubjectAction(permission);
    dataFilters[`${subject}-${action}`] = {...dataFilters[`${subject}-${action}`], ...filter};
    abilityBuilder.can(action, subject, enforce);
  };

  const deny = (
    permission: T,
    enforce?: PermissionEnforceType
  ) => {
    const { subject, action } = permissionToSubjectAction(permission);
    abilityBuilder.cannot(action, subject, enforce);
  };

  const build = () => {
    const ability = abilityBuilder.build();
    
    return {
      getDataFiltersFor: (permission:T):PermissionDataFilter => {
        const { subject, action } = permissionToSubjectAction(permission);
        const dataFiltersForPermission = dataFilters[`${subject}-${action}`];
        return {
          getForField: (fieldName: string) => {
            const permissionForField = dataFiltersForPermission?.[fieldName];
            if (!permissionForField)
              throw new Error(`Data filters for field "${fieldName}" when performing "${action}" on "${subject}" is not defined`);
            return permissionForField;
          }
        };
      },
      throwUnlessCan: (permission: T, data?: PermissionEnforceType) => {
        const { subject: targetSubject, action } = permissionToSubjectAction(
          permission
        );
        try {
          ForbiddenError.from(ability).throwUnlessCan(
            action,
            data ? subject(targetSubject, data) : targetSubject
          );
        } catch (err) {
          throw new Error(
            `Cannot perform "${action}" on "${targetSubject}" with ${JSON.stringify(
              data
            )} for user ${userContextMessage}. ${err}`
          );
        }
      },
      can: (permission: T, data?: PermissionEnforceType, debug?: (message: string) => void) => {
        const { subject: targetSubject, action } = permissionToSubjectAction(
          permission
        );
        const can = ability.can(
          action,
          data ? subject(targetSubject, data) : targetSubject
        );
        if (debug) {
          const { subject, action } = permissionToSubjectAction(
            permission
          );
          const rules = ability.rulesFor(action, subject);
          debug(`${can ? "Permitted" : "Not permitted"} to perform "${action}" on "${targetSubject}" with ${JSON.stringify(
            data
          )} for user ${userContextMessage}.`);
          debug(`Configured rules for ${permission}: ${JSON.stringify(rules)}`)
        }
        return can;
      }
    };
  }

  return { allow, deny, build }
}
